import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { CheckCircle2, Circle, UserPlus, FileText, Download, PartyPopper } from 'lucide-react';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { track } from '../utils/observability';

/**
 * ONBOARD-01 — Activation checklist on the dashboard.
 *
 * Renders only when `user.onboarding.completed_at` is null. Shows three
 * milestones with progressively-enabled CTAs. When all three become true,
 * shows a 3-second celebration then permanently hides itself by stamping
 * completed_at via PATCH /api/auth/onboarding-complete.
 *
 * Source of truth is the user object from AuthContext (which is hydrated by
 * /auth/me at login and refresh).
 */
export const OnboardingChecklist = () => {
  const { user, refreshUser } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [showCelebration, setShowCelebration] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [latestApprovedId, setLatestApprovedId] = useState(null);

  const onboarding = user?.onboarding;
  const m1 = Boolean(onboarding?.patient_created);
  const m2 = Boolean(onboarding?.doc_approved);
  const m3 = Boolean(onboarding?.pdf_exported);
  const done = m1 && m2 && m3;
  const alreadyCompleted = Boolean(onboarding?.completed_at);
  const completedCount = [m1, m2, m3].filter(Boolean).length;

  // Find most recent approved consultation so Milestone 3 CTA can deep-link.
  useEffect(() => {
    let mounted = true;
    if (!m2 || m3) return undefined;
    (async () => {
      try {
        const consultations = await api.getConsultations();
        const approved = (consultations || []).find(
          (c) => c.status === 'approved' || c.status === 'exported'
        );
        if (mounted && approved) setLatestApprovedId(approved.id);
      } catch {
        // best-effort — CTA falls back to /consultations list page
      }
    })();
    return () => { mounted = false; };
  }, [m2, m3]);

  // Trigger celebration + stamp completed_at the first time all 3 hit true.
  useEffect(() => {
    if (!done || alreadyCompleted || hidden) return undefined;
    setShowCelebration(true);
    const finalize = setTimeout(async () => {
      try { await api.completeOnboarding(); } catch { /* best-effort */ }
      try { await refreshUser(); } catch { /* best-effort */ }
      setHidden(true);
    }, 3000);
    return () => clearTimeout(finalize);
  }, [done, alreadyCompleted, hidden, refreshUser]);

  if (alreadyCompleted || hidden) return null;
  // If user has no onboarding object (loading state), don't flash an empty card.
  if (!onboarding) return null;

  const cardBg = isDark
    ? 'bg-slate-800/50 border-slate-700'
    : 'bg-white border-slate-200';
  const muted = isDark ? 'text-slate-400' : 'text-slate-600';

  if (showCelebration) {
    return (
      <div
        className={`mb-6 rounded-xl border p-6 text-center ${cardBg}`}
        data-testid="onboarding-celebration"
      >
        <PartyPopper className="w-12 h-12 mx-auto mb-3 text-amber-500" />
        <h3 className={`text-lg font-semibold ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
          🎉 Parabéns! Você explorou o Akali AI completo.
        </h3>
        <p className={`text-sm mt-2 mb-4 ${muted}`}>
          Você dominou os fundamentos. Bora documentar de verdade?
        </p>
        <Button
          onClick={() => {
            track('onboarding_celebration_cta_clicked');
            navigate('/patients');
          }}
          className="bg-primary hover:bg-primary-hover"
          data-testid="onboarding-celebration-cta"
        >
          Criar nova consulta →
        </Button>
      </div>
    );
  }

  return (
    <div
      className={`mb-6 rounded-xl border p-5 ${cardBg}`}
      data-testid="onboarding-checklist"
    >
      <div className="flex items-end justify-between mb-3">
        <div>
          <h3 className={`text-base font-semibold ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
            Primeiros passos
          </h3>
          <p className={`text-xs mt-0.5 ${muted}`}>
            Complete para desbloquear o potencial completo do Akali AI
          </p>
        </div>
        <div className={`text-xs font-medium ${muted}`} data-testid="onboarding-progress-label">
          {completedCount} de 3 concluídos
        </div>
      </div>

      <div className={`h-1.5 rounded-full overflow-hidden mb-5 ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>
        <div
          className="h-full bg-gradient-to-r from-orange-400 to-purple-500 transition-all duration-300"
          style={{ width: `${(completedCount / 3) * 100}%` }}
          data-testid="onboarding-progress-bar"
        />
      </div>

      <ul className="space-y-3">
        <MilestoneRow
          testId="onboarding-m1"
          done={m1}
          Icon={UserPlus}
          title="Crie seu primeiro paciente"
          description="Cadastre um paciente para começar a documentar suas consultas."
          ctaLabel="Criar paciente"
          ctaDisabled={false}
          onCta={() => navigate('/patients/new')}
        />
        <MilestoneRow
          testId="onboarding-m2"
          done={m2}
          Icon={FileText}
          title="Gere sua primeira documentação"
          description="Grave um áudio e deixe a IA estruturar o prontuário por você."
          ctaLabel="Iniciar consulta"
          ctaDisabled={!m1}
          ctaTooltip={!m1 ? 'Complete o passo 1 primeiro' : null}
          onCta={() => navigate('/patients')}
        />
        <MilestoneRow
          testId="onboarding-m3"
          done={m3}
          Icon={Download}
          title="Exporte seu primeiro PDF"
          description="Baixe o prontuário gerado e veja a qualidade do documento final."
          ctaLabel="Exportar PDF"
          ctaDisabled={!m2}
          ctaTooltip={!m2 ? 'Complete o passo 2 primeiro' : null}
          onCta={() => navigate(latestApprovedId ? `/consultations/${latestApprovedId}` : '/consultations')}
        />
      </ul>
    </div>
  );
};

const MilestoneRow = ({ testId, done, Icon, title, description, ctaLabel, ctaDisabled, ctaTooltip, onCta }) => {
  const { isDark } = useTheme();
  const titleColor = isDark ? 'text-slate-100' : 'text-slate-900';
  const muted = isDark ? 'text-slate-400' : 'text-slate-600';
  return (
    <li className="flex items-start gap-3" data-testid={testId}>
      <div className="mt-0.5 shrink-0" data-testid={`${testId}-status`}>
        {done ? (
          <CheckCircle2 className="w-5 h-5 text-emerald-500" />
        ) : (
          <Circle className={`w-5 h-5 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
        )}
      </div>
      <div className="shrink-0 mt-0.5">
        <Icon className={`w-4 h-4 ${done ? 'text-emerald-500' : muted}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm font-medium ${done ? 'line-through ' + muted : titleColor}`}>
            {title}
          </span>
          {done && (
            <span className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
              Concluído
            </span>
          )}
        </div>
        <p className={`text-xs mt-0.5 ${muted}`}>{description}</p>
      </div>
      {!done && (
        <div className="shrink-0">
          <Button
            size="sm"
            variant="outline"
            disabled={ctaDisabled}
            onClick={onCta}
            title={ctaTooltip || undefined}
            data-testid={`${testId}-cta`}
          >
            {ctaLabel} →
          </Button>
        </div>
      )}
    </li>
  );
};

export default OnboardingChecklist;
